import {
  ModalContent,
  SizingModalType,
  SizingResult,
  ProjectDetails,
  SizingFormData,
} from "../types";
import { getMonthName, formatShortMonthDate } from "@/utils/formatDate";

export const getModalContent = (
  type: SizingModalType,
  sizingResult: SizingResult | null,
  sizingError: string | null,
  projectDetails:
    | ProjectDetails
    | (Partial<ProjectDetails> &
        Pick<ProjectDetails, "accountHash" | "customerName">)
): ModalContent => {
  if (sizingResult?.calculationFailed) {
    // Check if the backend error message contains "6 months" pattern
    const errorMessage = sizingResult.errorMessage || "";
    const contains6Months =
      errorMessage.toLowerCase().includes("6 months") ||
      errorMessage.toLowerCase().includes("six months") ||
      errorMessage.toLowerCase().includes("less than 6") ||
      errorMessage.toLowerCase().includes("insufficient");

    if (contains6Months) {
      return {
        title: "System Sizing Tool",
        mainMessage: "Insufficient Consumption Data for Automatic Sizing",
        description:
          "This customer has less than 6 months of usage history with Community Power, so we can't run an automatic analysis.",
        subDescription: null,
        nextSteps: [
          `If the customer can provide at least 6 months of Green Button data from SDG&E for this service address, please complete and upload the Excel-based System Sizing Tool (located in the Templates folder).`,
        ],
        showUpload: true,
        infoText: null,
        isSuccess: false,
      };
    } else {
      return {
        title: "System Sizing Tool",
        mainMessage: "Calculation Failed",
        description:
          errorMessage ||
          "An error occurred during calculation. Please try again or contact support.",
        subDescription: null,
        nextSteps: [
          "Please contact support or try uploading a System Sizing Tool output file for analysis.",
        ],
        showUpload: true,
        infoText: null,
        isSuccess: false,
      };
    }
  }

  if (sizingResult?.isSufficient !== undefined) {
    const isSufficient = sizingResult.isSufficient;
    const resultMessage = sizingResult.resultMessage || sizingResult.message;
    // SDCP-1325: an existing-solar-only non-pass is a real failure — the portal's tool
    // computes that scenario correctly, so it is not sent for manual review.
    const hasFailed = sizingResult.sizing_status === "failed";

    return {
      title: "Project Review Result",
      // SDCP-1338 (per Emily): any other project that does not pass is marked PENDING for
      // manual review in the offline Excel tool, not a hard "insufficient — modify size" fail.
      mainMessage: isSufficient
        ? "Project size is sufficient"
        : hasFailed
        ? "Project size failed"
        : "Project size is pending manual review",
      description: isSufficient
        ? null
        : hasFailed
        ? resultMessage || "The proposed system did not pass the sizing tool."
        : resultMessage || "Flagged for manual review in the offline Excel tool.",
      subDescription: `${projectDetails?.accountHash || "N/A"} • ${
        projectDetails?.customerName || "N/A"
      }`,
      nextSteps: null,
      showUpload: false,
      infoText: null,
      isSuccess: isSufficient,
    };
  }

  if (sizingResult?.message || sizingResult?.resultMessage) {
    const message = sizingResult.resultMessage || sizingResult.message || "";
    return {
      title: "Project Review Result",
      mainMessage: message,
      description: message,
      subDescription: `${projectDetails?.accountHash || "N/A"} • ${
        projectDetails?.customerName || "N/A"
      }`,
      nextSteps: null,
      showUpload: false,
      infoText: null,
      isSuccess: message.toLowerCase().includes("sufficient"),
    };
  }

  if (sizingError) {
    return {
      title: "System Sizing Tool",
      mainMessage: sizingError,
      description: null,
      subDescription: null,
      nextSteps: null,
      showUpload: true,
      infoText: null,
      isSuccess: false,
    };
  }

  const contentMap: Record<SizingModalType, ModalContent> = {
    form: {
      title: "",
      mainMessage: "",
      description: null,
      subDescription: null,
      nextSteps: null,
      showUpload: false,
      infoText: null,
      isSuccess: false,
    },
    success: {
      title: "Project Review Result",
      mainMessage: "Project size is sufficient",
      description: null,
      subDescription: `${projectDetails?.accountHash || "N/A"} • ${
        projectDetails?.customerName || "N/A"
      }`,
      nextSteps: null,
      showUpload: false,
      infoText: null,
      isSuccess: true,
    },
    insufficient: {
      title: "Project Review Result",
      // SDCP-1338 (per Emily): pending manual review instead of a hard insufficient fail.
      mainMessage: "Project size is pending manual review",
      description: "Flagged for manual review in the offline Excel tool.",
      subDescription: `${projectDetails?.accountHash || "N/A"} • ${
        projectDetails?.customerName || "N/A"
      }`,
      nextSteps: null,
      showUpload: false,
      infoText: null,
      isSuccess: false,
    },
    genericError: {
      title: "System Sizing Tool",
      mainMessage:
        "We're unable to complete the automatic sizing for this project",
      description: null,
      subDescription: null,
      nextSteps: null,
      showUpload: true,
      infoText: null,
      isSuccess: false,
    },
    insufficientData: {
      title: "System Sizing Tool",
      mainMessage: "Insufficient Consumption Data for Automatic Sizing",
      description:
        "This customer has less than 6 months of usage history with Community Power, so we can't run an automatic analysis.",
      subDescription: null,
      nextSteps: [
        "If the customer can provide at least 6 months of Green Button data from SDG&E for this service address (available at https://www.sdge.com/green-button), please complete and upload the System Sizing Tool output.",
      ],
      showUpload: true,
      infoText:
        "If not, apply a Solar-to-Battery ratio limit of 1:2.5, where the battery's usable capacity (kWh) must not exceed 2.5 times the solar system's AC power rating (kW-AC).",
      isSuccess: false,
    },
    accountNotFound: {
      title: "System Sizing Tool",
      mainMessage: "Account Number Not Found or No Data Available",
      description: "We couldn't locate data for this Account Number.",
      subDescription: null,
      nextSteps: [
        "Please verify the Account Number and try again.",
        "If the customer is new to Community Power, they must provide at least 6 months of Green Button data from SDG&E for this service address (available at https://www.sdge.com/green-button).",
        "Once available, complete and upload the System Sizing Tool output for analysis.",
      ],
      showUpload: true,
      infoText:
        "If not, size the project using the Simple Ratio Method with a minimum Solar-to-Battery ratio of 0.5.",
      isSuccess: false,
    },
    calculationFailure: {
      title: "System Sizing Tool",
      mainMessage: "Insufficient Consumption Data for Automatic Sizing",
      description:
        "This customer has less than 6 months of usage history with Community Power, so we can't run an automatic analysis.",
      subDescription: null,
      nextSteps: [
        `If the customer can provide at least 6 months of Green Button data from SDG&E for this service address, please complete and upload the Excel-based System Sizing Tool (located in the Templates folder).`,
      ],
      showUpload: true,
      infoText: null,
      isSuccess: false,
    },
  };

  return contentMap[type] || contentMap.genericError;
};

/**
 * Creates a minimal ProjectDetails object with only accountHash and customerName
 * Used as a fallback when full project details are not available
 */
export const createMinimalProjectDetails = (
  accountHash?: string,
  customerName?: string
): Partial<ProjectDetails> &
  Pick<ProjectDetails, "accountHash" | "customerName"> => ({
  accountHash: accountHash || "N/A",
  customerName: customerName || "N/A",
});

/**
 * Creates an empty SizingFormData object with all fields initialized to their default empty values.
 * @returns A new SizingFormData object with empty values
 */
export const createEmptySizingFormData = (): SizingFormData => ({
  accountNumber: "",
  zipCode: "",
  customerName: "",
  solarSystemType: [],
  existingSolarKwdc: "",
  existingSolarKwac: "",
  existingSolarAnnualProduction: "",
  existingSolarLessThan12Months: "",
  newSolarKwdc: "",
  newSolarKwac: "",
  newSolarAnnualProduction: "",
  hasExistingBattery: false,
  batteryManufacturer: "",
  batteryManufacturerId: "",
  batteryProductName: "",
  batteryQuantity: "",
  nominalPower: "",
  usableCapacity: "",
  totalBatteryUsableCapacity: "",
  totalNominalPower: "",
});

/**
 * Helper to format solar capacity (uses DC capacity)
 */
const formatSolarCapacity = (
  kwdc: number | string | undefined | null
): string => {
  if (kwdc == null || kwdc === "") return "N/A";
  return String(kwdc).trim() || "N/A";
};

/**
 * Gets the battery configuration string combining manufacturer and product name
 */
export const getBatteryConfiguration = (
  projectDetails: ProjectDetails | null | undefined,
  sizingFormData?: SizingFormData | null
): string => {
  const manufacturer =
    projectDetails?.batteryOem || sizingFormData?.batteryManufacturer || "";
  const productName =
    projectDetails?.batteryModel || sizingFormData?.batteryProductName || "";

  if (manufacturer && productName) {
    return `${manufacturer} ${productName}`;
  }
  return manufacturer || productName || "N/A";
};

/**
 * Builds the project details section for copy functionality
 */
export const buildProjectDetailsSection = (
  projectDetails: ProjectDetails | null | undefined,
  sizingResult: SizingResult | null,
  sizingFormData?: SizingFormData | null
): string[] => {
  const projectId = projectDetails?.id?.toString()?.padStart(4, "0") || "N/A";
  const customerName =
    projectDetails?.customerName || sizingFormData?.customerName || "N/A";
  const batteryConfig = getBatteryConfiguration(projectDetails, sizingFormData);
  const batteryQty =
    projectDetails?.numberOfBatteries?.toString() ||
    sizingFormData?.batteryQuantity ||
    "N/A";
  const existingCapacity = formatSolarCapacity(
    projectDetails != null ? projectDetails.solarKwdc : sizingFormData?.existingSolarKwdc
  );
  const newCapacity = formatSolarCapacity(
    projectDetails != null ? projectDetails.newSolarKwdc : sizingFormData?.newSolarKwdc
  );
  const analysisDateRaw =
    sizingResult?.analysisDate || new Date().toISOString().split("T")[0];
  const analysisDate = formatShortMonthDate(analysisDateRaw);

  return [
    "SYSTEM SIZING TOOL DATA",
    "=".repeat(50),
    "",
    "Project Details:",
    `Project ID: ${projectId}`,
    `Project Name: ${customerName}`,
    `Battery Configuration: ${batteryConfig}`,
    `Battery Quantity: ${batteryQty}`,
    `Existing Solar kW-DC: ${existingCapacity}`,
    `New Solar kW-DC: ${newCapacity}`,
    `Analysis Date: ${analysisDate}`,
    "",
  ];
};

/**
 * Builds the review result section for copy functionality
 */
export const buildReviewResultSection = (content: ModalContent): string[] => {
  return [
    "Project Review Result:",
    content.mainMessage,
    ...(content.subDescription ? [content.subDescription] : []),
    "",
  ];
};

/**
 * Builds the monthly breakdown section for copy functionality
 */
export const buildMonthlySection = (
  sizingResult: SizingResult | null
): string[] => {
  if (!sizingResult?.monthlyResults?.length) {
    return [];
  }

  const header = [
    "Monthly Breakdown:",
    "-".repeat(50),
    "Month\tDaily Average Excess/Deficit (kWh)\tStatus",
    "-".repeat(50),
  ];

  const rows = Array.from({ length: 12 }, (_, i) => i + 1).map(
    (monthNumber) => {
      const monthData = sizingResult.monthlyResults?.find(
        (m) => m.month === monthNumber
      );
      let value = "N/A";
      if (monthData) {
        if (monthData.averageDailyExcessPerMonth > 0) {
          value = monthData.averageDailyExcessPerMonth.toFixed(1);
        } else if (monthData.combinedExcessAndProduction !== 0) {
          value = monthData.combinedExcessAndProduction.toFixed(1);
        }
      }
      const status = monthData?.sufficient ? "Pass" : "Fail";
      return `${getMonthName(monthNumber)}\t${value}\t${status}`;
    }
  );

  return [...header, ...rows];
};
